import { useCallback, useEffect, useState } from "react";
import http from "../../../Utils/axiosConfig";
import { apiMessage } from "../../../Utils/apiMessage";
import "./Details.css";
import "../Main/Dashboard.css";
import { onlyNumber } from "../../../Utils/commonFuntion";
import ToastService from "../../../Utils/ToastService";
import { calculateMilkValues } from "../../../Utils/Calculations";

// Print via a hidden same-page iframe rather than window.open(): mobile browsers
// frequently block sized popup windows outright, and even when they don't, a
// popup's onload is unreliable and iOS Safari often refuses to print from a
// secondary window at all.
const printHtmlViaIframe = (html) => {
  let printFrame = document.getElementById("receipt-print-frame");
  if (!printFrame) {
    printFrame = document.createElement("iframe");
    printFrame.id = "receipt-print-frame";
    printFrame.style.position = "fixed";
    printFrame.style.right = "0";
    printFrame.style.bottom = "0";
    printFrame.style.width = "0";
    printFrame.style.height = "0";
    printFrame.style.border = "0";
    document.body.appendChild(printFrame);
  }

  const frameDoc = printFrame.contentWindow.document;
  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();

  printFrame.onload = () => {
    printFrame.contentWindow.focus();
    printFrame.contentWindow.print();
  };
};

const BillDetails = ({ current, mem_id, phone }) => {
  const [billData, setBillData] = useState([]);
  const [stats, setStats] = useState();
  const [customChart, setCustomChart] = useState([]);
  const [formData, setFormData] = useState({
    advance_debit: 0,
    bank: 0,
    supply_debit: 0,
    balance: 0,
  });

  // चालू (ongoing) period can never have been billed yet; मागील (previous)
  // is only editable if its bill hasn't been generated/saved yet.
  const isEditable = current || !stats?.isBillGenerated;
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ qty: "", fat: "", snf: "" });
  const [savingId, setSavingId] = useState(null);

  const totalAmount = parseFloat(stats?.total_amount ?? 0);
  const totalDeduction =
    parseFloat(formData.advance_debit || 0) +
    parseFloat(formData.bank || 0) +
    parseFloat(formData.supply_debit || 0);
  const remainingSupplyBalance = stats?.isBillGenerated
    ? parseFloat(stats?.supBalance ?? 0)
    : parseFloat(stats?.supBalance ?? 0) - parseFloat(formData.supply_debit || 0);
  const remainingAdvanceBalance = stats?.isBillGenerated
    ? parseFloat(stats?.advBalance ?? 0)
    : parseFloat(stats?.advBalance ?? 0) - parseFloat(formData.advance_debit || 0);
  const grossAmount = totalAmount - totalDeduction;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getBillingDate = () => {
    const today = new Date();
    const toLocal = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    if (current) {
      return toLocal(today);
    }
    const day = today.getDate();
    let targetDay;
    if (day >= 1 && day <= 10) {
      targetDay = new Date(today.getFullYear(), today.getMonth() - 1, 21);
    } else if (day >= 11 && day <= 20) {
      targetDay = new Date(today.getFullYear(), today.getMonth(), 1);
    } else {
      targetDay = new Date(today.getFullYear(), today.getMonth(), 11);
    }
    return toLocal(targetDay);
  };
  const fetchData = useCallback(async () => {
    try {
      const formattedDate = getBillingDate();
      const dairy_id = localStorage.getItem("temp_key");
      const [response, customChartResponse] = await Promise.all([
        http.post("milk_data/get_ten_day_data", {
          id: dairy_id,
          mem_id: mem_id,
          date: formattedDate,
          isBill: !current,
        }),
        http.post('rate-chart/get')
      ]);

      if (response.code === "DATA_FOUND") {
        setBillData(response.data.records || []);
        const s = response.data.stats || {
          advBalance: 0,
          supBalance: 0,
          total_amount: 0,
          total_milk: 0,
        };
        setStats(s);
        if (s.isBillGenerated) {
          setFormData((prev) => ({
            ...prev,
            advance_debit: s.advance_debit ?? 0,
            supply_debit: s.supply_debit ?? 0,
            balance: s.supBalance ?? 0,
            bank: s.bank ?? 0,
          }));
        } else {
          setFormData({
            advance_debit: 0,
            bank: s.bank,
            supply_debit: 0,
            balance: s.supBalance ?? 0,
          });
        }
      }

      // Load custom chart
      if (customChartResponse?.code === "DATA_FOUND") {
        setCustomChart(customChartResponse.data.chart || []);
      }
    } catch (error) {
      console.error("Error fetching:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, mem_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const startEdit = (record) => {
    setEditingId(record.id);
    setEditForm({ qty: String(record.qty), fat: String(record.fat), snf: String(record.snf) });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ qty: "", fat: "", snf: "" });
  };

  const [pendingChange, setPendingChange] = useState(null);

  // Validates + computes the new rate, then hands off to a confirm popup
  // showing before → after instead of saving immediately.
  const requestSave = (record) => {
    const { qty, fat, snf } = editForm;
    if (!qty || !fat || !snf) {
      ToastService.warning("कृपया सर्व फील्ड भरा!");
      return;
    }
    const calc = calculateMilkValues({ qty, fat, snf, type: record.type, customChart });
    if (calc.error) {
      ToastService.warning("अवैध फॅट किंवा एस एन एफ मूल्य!");
      return;
    }
    setPendingChange({ record, qty, fat, snf, calc });
  };

  const cancelConfirm = () => setPendingChange(null);

  const confirmSave = async () => {
    const { record, qty, fat, snf, calc } = pendingChange;
    setSavingId(record.id);
    try {
      const response = await http.post("milk_data/update_milk_entry", {
        id: record.id,
        mst_id: localStorage.getItem("temp_key"),
        qty,
        fat,
        snf,
        rate: calc.rate,
        total_rate: calc.t_rate,
        en_milking_shift: record.en_milking_shift,
      });
      if (response?.code === "ENTRY_UPDATED") {
        ToastService.success("नोंद अपडेट झाली ✅");
        setPendingChange(null);
        cancelEdit();
        fetchData();
      } else if (response?.code === "ENTRY_ALREADY_BILLED") {
        ToastService.error("या नोंदीचे बिल आधीच तयार झाले आहे, संपादन करता येणार नाही.");
        setPendingChange(null);
        cancelEdit();
        fetchData();
      } else {
        ToastService.error(apiMessage(response, "अपडेट अयशस्वी ❌"));
      }
    } catch (error) {
      console.error("Error updating entry:", error);
      ToastService.error(apiMessage());
    } finally {
      setSavingId(null);
    }
  };

  const onFormSubmit = async () => {
    let body = {
      ...formData,
      balance: remainingSupplyBalance,
      advance_balance: remainingAdvanceBalance,
      totalmilk: stats.total_milk,
      totalRate: totalAmount,
      cutting: totalDeduction,
      grandTotal: grossAmount,
      date: getBillingDate(),
      billingDate: getBillingDate(),
      mem_id: mem_id,
      id: localStorage.getItem("temp_key"),
    };
    try {
      let response = await http.post("milk_data/post_bill", body);
      if (response.code === "BILL_GENERATED") {
        ToastService.success("बिल जतन झाले ✅");
        setStats((prev) => ({
          ...prev,
          isBillGenerated: true,
          advBalance: remainingAdvanceBalance,
          advance_debit: body.advance_debit,
          supply_debit: body.supply_debit,
          supBalance: body.balance,
          bank: body.bank,
          totalRate: body.totalRate,
          totalmilk: body.totalmilk,
          cutting: body.cutting,
          grandTotal: body.grandTotal,
        }));
      } else {
        ToastService.error("बिल जतन अयशस्वी ❌: " + (response.message || "Unknown error"));
      }
    } catch (error) {
      console.error("Error posting bill:", error);
      ToastService.error(apiMessage());
    }
  };

  const handleWhatsAppShare = () => {
    const memberName = stats?.mem_name || "";
    const memberId = stats?.dairy_id || mem_id || "";
    const billingPeriod = getBillingDate();

    const message = `🥛 दूध बिल - ${billingPeriod}
सभासद क्र.: ${memberId}${memberName ? `\nनाव: ${memberName}` : ""}
एकूण दूध: ${Number(stats?.total_milk)?.toFixed(2)} लि.
एकूण रक्कम: ₹${totalAmount.toFixed(2)}
ऍडव्हान्स: ₹${parseFloat(formData.advance_debit || 0).toFixed(2)}
बँक भरणा: ₹${parseFloat(formData.bank || 0).toFixed(2)}
पशुखाद्य: ₹${parseFloat(formData.supply_debit || 0).toFixed(2)}
एकूण कपात: ₹${totalDeduction.toFixed(2)}
देय रक्कम: ₹${grossAmount.toFixed(2)}`;

    const memberPhone = phone?.replace(/\D/g, "");
    const target = memberPhone ? `91${memberPhone}` : "";
    window.open(`https://wa.me/${target}?text=${encodeURIComponent(message)}`, "_blank");
  };

  const onFormPrint = () => {
    const dairyName = localStorage.getItem("dairy_name") || "डेअरी";
    const memberName = stats?.mem_name || "";
    const memberId = stats?.dairy_id || mem_id || "";

    const tableRows = billData
      .map(
        (r) => `
        <tr>
          <td>${formatDate(r.date)}</td>
          <td>${r.mr_milking_shift}</td>
          <td>${r.qty}</td>
          <td>${r.fat}</td>
          <td>${r.snf}</td>
          <td>${r.rate}</td>
          <td>${r.total_rate}</td>
        </tr>`,
      )
      .join("");

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8" />
        <title>Bill</title>
        <style>
          @page { margin: 0; size: 72mm auto; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Courier New', monospace; font-size: 11px; width: 72mm; padding: 4mm; }
          .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 4px; margin-bottom: 4px; }
          .header h2 { font-size: 14px; font-weight: bold; }
          .header p { font-size: 11px; }
          .member-info { margin-bottom: 4px; font-size: 11px; }
          .member-info span { display: inline-block; }
          table { width: 100%; border-collapse: collapse; font-size: 10px; }
          th { border-top: 1px dashed #000; border-bottom: 1px dashed #000; padding: 2px 1px; text-align: center; font-weight: bold; }
          td { padding: 2px 1px; text-align: center; }
          .totals { border-top: 1px dashed #000; margin-top: 4px; padding-top: 4px; }
          .totals-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 5px 4px; padding: 4px 0; }
          .totals-grid .cell { display: flex; flex-direction: column; }
          .totals-grid .cell label { font-size: 9px; color: #444; }
          .totals-grid .cell span { font-weight: bold; font-size: 11px; }
          .gross-section { border-top: 1px dashed #000; margin-top: 4px; padding-top: 4px; }
          .gross-section .deduction { display: flex; justify-content: space-between; font-size: 10px; margin-bottom: 3px; }
          .gross-section .gross { display: flex; justify-content: space-between; font-weight: bold; font-size: 13px; }
          .footer { text-align: center; border-top: 1px dashed #000; margin-top: 6px; padding-top: 4px; font-size: 10px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>${dairyName}</h2>
          <p>दूध बिल</p>
        </div>
        <div class="member-info">
          <div><span>सभासद क्र.: ${memberId}</span></div>
          ${memberName ? `<div><span>नाव: ${memberName}</span></div>` : ""}
          <div><span>दिनांक: ${new Date().toLocaleDateString("en-IN")}</span></div>
        </div>
        <table>
          <thead>
            <tr>
              <th>दिनांक</th>
              <th>वेळ</th>
              <th>लि.</th>
              <th>फॅट</th>
              <th>SNF</th>
              <th>दर</th>
              <th>रक्कम</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
        <div class="totals">
          <div class="totals-grid">
            <div class="cell"><label>एकूण दूध</label><span>${Number(stats?.total_milk)?.toFixed(2)} लि.</span></div>
            <div class="cell"><label>ऍडव्हान्स</label><span>₹${parseFloat(formData.advance_debit || 0).toFixed(2)}</span></div>
            ${Number(remainingAdvanceBalance.toFixed(2)) !== 0 ? `<div class="cell"><label>शि.ऍडव्हान्स</label><span>₹${remainingAdvanceBalance.toFixed(2)}</span></div>` : ""}
            <div class="cell"><label>बँक भरणा</label><span>₹${parseFloat(formData.bank || 0).toFixed(2)}</span></div>
            <div class="cell"><label>पशुखाद्य</label><span>₹${parseFloat(formData.supply_debit || 0).toFixed(2)}</span></div>
            <div class="cell"><label>शि.पशुखाद्य</label><span>₹${remainingSupplyBalance.toFixed(2)}</span></div>
          </div>
          <div class="gross-section">
            <div class="deduction"><span>एकूण रक्कम</span><span>₹${totalAmount.toFixed(2)}</span></div>
            <div class="deduction"><span>एकूण कपात</span><span>- ₹${totalDeduction.toFixed(2)}</span></div>
            <div class="gross"><span>देय रक्कम</span><span>₹${grossAmount.toFixed(2)}</span></div>
          </div>
        </div>
        <div class="footer"><p>धन्यवाद!</p></div>
      </body>
      </html>`;

    printHtmlViaIframe(printContent);
  };

  return (
    <div className="table-container">
      {billData.length > 0 ? (
        <>
          <div className="table-wrapper">
            <table className="emc-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Shift</th>
                  <th>Qty (L)</th>
                  <th>Fat</th>
                  <th>SNF</th>
                  <th>Rate</th>
                  <th>Total</th>
                  {isEditable && <th>Edit</th>}
                </tr>
              </thead>
              <tbody>
                {billData.map((record, index) => {
                  const isEditingRow = editingId === record.id;
                  const liveCalc = isEditingRow
                    ? calculateMilkValues({ qty: editForm.qty, fat: editForm.fat, snf: editForm.snf, type: record.type, customChart })
                    : null;
                  return (
                    <tr key={index}>
                      <td>{formatDate(record.date)}</td>
                      <td>{record.mr_milking_shift}</td>
                      <td>
                        {isEditingRow ? (
                          <input
                            type="number"
                            className="form-input"
                            value={editForm.qty}
                            onChange={(e) => setEditForm((p) => ({ ...p, qty: e.target.value }))}
                            onKeyDown={onlyNumber}
                          />
                        ) : record.qty}
                      </td>
                      <td>
                        {isEditingRow ? (
                          <input
                            type="number"
                            className="form-input"
                            value={editForm.fat}
                            onChange={(e) => setEditForm((p) => ({ ...p, fat: e.target.value }))}
                            onKeyDown={onlyNumber}
                          />
                        ) : record.fat}
                      </td>
                      <td>
                        {isEditingRow ? (
                          <input
                            type="number"
                            className="form-input"
                            value={editForm.snf}
                            onChange={(e) => setEditForm((p) => ({ ...p, snf: e.target.value }))}
                            onKeyDown={onlyNumber}
                          />
                        ) : record.snf}
                      </td>
                      <td>{isEditingRow ? (liveCalc?.error ? "-" : `₹${liveCalc.rate}`) : `₹${record.rate}`}</td>
                      <td>{isEditingRow ? (liveCalc?.error ? "-" : `₹${liveCalc.t_rate}`) : `₹${record.total_rate}`}</td>
                      {isEditable && (
                        <td>
                          {isEditingRow ? (
                            <>
                              <button
                                className="bd-icon-btn"
                                disabled={savingId === record.id}
                                onClick={() => requestSave(record)}
                              >
                                ✓
                              </button>
                              <button className="bd-icon-btn" onClick={cancelEdit}>✕</button>
                            </>
                          ) : (
                            <button className="bd-icon-btn" onClick={() => startEdit(record)}>✎</button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {current ? (
            <></>
          ) : (
            <>
              <div className="bill-summary-form">
                <div className="summary-grid">
                  <div className="summary-row">
                    <div>एकूण दूध</div>{" "}
                    <div className="summary-value">{stats.total_milk} लि</div>
                  </div>
                  <div className="summary-row">
                    <div>एकूण रक्कम</div>{" "}
                    <div className="summary-value">
                      {stats.total_amount} रु.
                    </div>
                  </div>
                  <div className="summary-field">
                    <label className="form-label">ऍडव्हान्स(रु.)</label>
                    <input
                      type="number"
                      min={0}
                      value={formData.advance_debit}
                      readOnly={stats?.isBillGenerated}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          advance_debit: e.target.value,
                        }))
                      }
                      onKeyDown={onlyNumber}
                      className="form-input"
                    />
                  </div>
                  <div className="summary-field">
                    <label>शि.ऍडव्हान्स</label>
                    <input
                      type="number"
                      value={remainingAdvanceBalance.toFixed(2)}
                      className="form-input"
                      readOnly
                    />
                  </div>
                  <div className="summary-field">
                    <label>पशुखाद्य(रु.)</label>
                    <input
                      type="number"
                      min={0}
                      value={formData.supply_debit}
                      readOnly={stats?.isBillGenerated}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          supply_debit: e.target.value,
                        }))
                      }
                      onKeyDown={onlyNumber}
                      className="form-input"
                    />
                  </div>
                  <div className="summary-field">
                    <label>शि.पशुखाद्य</label>
                    <input
                      type="number"
                      value={remainingSupplyBalance.toFixed(2)}
                      className="form-input"
                      readOnly
                    />
                  </div>
                  <div className="summary-field">
                    <label className="form-label">बँक भरणा (रु.)</label>
                    <input
                      type="number"
                      value={formData.bank}
                      className="form-input"
                      readOnly
                    />
                  </div>
                </div>
              </div>
              <div className="bill-totals">
                <div className="bill-total-row">
                  <span>एकूण रक्कम</span>
                  <span>₹{totalAmount.toFixed(2)}</span>
                </div>
                <div className="bill-total-row deduction">
                  <span>एकूण कपात</span>
                  <span>- ₹{totalDeduction.toFixed(2)}</span>
                </div>
                <div className="bill-total-row gross">
                  <span>देय रक्कम</span>
                  <span>₹{grossAmount.toFixed(2)}</span>
                </div>
              </div>
              <div className="action-section">
                {stats?.isBillGenerated ? (
                  <>
                    <button className="submitbtn active" onClick={onFormPrint}>
                      Print
                    </button>
                    &nbsp;&nbsp;
                    <button
                      className="submitbtn whatsapp"
                      onClick={handleWhatsAppShare}
                    >
                      WhatsApp
                    </button>
                  </>
                ) : (
                  <button className="submitbtn active" onClick={onFormSubmit}>
                    Save
                  </button>
                )}
              </div>
            </>
          )}
        </>
      ) : (
        <div className="no-data">
          <p>No records found for the selected period.</p>
        </div>
      )}

      {pendingChange && (
        <div className="bd-confirm-overlay" onClick={cancelConfirm}>
          <div className="bd-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <h3>बदल जतन करायचे आहेत का?</h3>
            <table className="bd-confirm-table">
              <thead>
                <tr><th></th><th>आधी</th><th>नंतर</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>लिटर</td>
                  <td>{pendingChange.record.qty}</td>
                  <td className={String(pendingChange.record.qty) !== pendingChange.qty ? "bd-changed" : ""}>{pendingChange.qty}</td>
                </tr>
                <tr>
                  <td>फॅट</td>
                  <td>{pendingChange.record.fat}</td>
                  <td className={String(pendingChange.record.fat) !== pendingChange.fat ? "bd-changed" : ""}>{pendingChange.fat}</td>
                </tr>
                <tr>
                  <td>SNF</td>
                  <td>{pendingChange.record.snf}</td>
                  <td className={String(pendingChange.record.snf) !== pendingChange.snf ? "bd-changed" : ""}>{pendingChange.snf}</td>
                </tr>
                <tr>
                  <td>दर/लि.</td>
                  <td>₹{pendingChange.record.rate}</td>
                  <td className={String(pendingChange.record.rate) !== String(pendingChange.calc.rate) ? "bd-changed" : ""}>₹{pendingChange.calc.rate}</td>
                </tr>
                <tr>
                  <td>एकूण रक्कम</td>
                  <td>₹{pendingChange.record.total_rate}</td>
                  <td className={String(pendingChange.record.total_rate) !== String(pendingChange.calc.t_rate) ? "bd-changed" : ""}>₹{pendingChange.calc.t_rate}</td>
                </tr>
              </tbody>
            </table>
            <div className="bd-confirm-actions">
              <button className="submitbtn active" disabled={savingId === pendingChange.record.id} onClick={confirmSave}>
                {savingId === pendingChange.record.id ? "जतन होत आहे..." : "पुष्टी करा"}
              </button>
              <button className="submitbtn secondary" onClick={cancelConfirm}>रद्द करा</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillDetails;
